import React from 'react';
import {PropTypes} from 'prop-types';
import {Link} from '../../shared/link';
import numeral from 'numeral';
import {MakeApiCall, WriteConsoleErrors, ReplaceUrl} from '../../shared/utils';
import {ErrorsBlockComponent} from '../../shared/errorsBlockComponent';
import {RefreshButtonComponent} from '../../shared/refreshButtonComponent';
import {AccountComponent} from '../AccountsListComponent/account';
import {CreditCardCaptionComponent} from '../../shared/creditCardComponents';
import {InfoItem} from '../AccountInfo/infoitem';
import {DateTimeItem} from '../AccountInfo/datetimeitem';
import {PAYMENT_STATE, UrlBuilder} from '../../app.defs';
import {EditNotesDialogComponent} from '../../shared/EditNotesDialog';
import {PageHeader} from '../../shared/PageHeader';

const PAYMENTINFO_ACTION = {
    NONE: 0,
    LOADING: 1,
    EDITNOTES: 2,
}

export class PaymentInfoComponent extends React.Component {
    constructor(props, ...others) {
        super(props, ...others);
        this.state = {
            paymentId: props.paymentId,
            isLoaded: false,
            action: PAYMENTINFO_ACTION.NONE,
            errors: null,
            paymentInfo: null,
            requestId: 0
        }

        this.doLoad = this.doLoad.bind(this);
        this.handleEditNotesDialogHide = this.handleEditNotesDialogHide.bind(this);
        this.doSaveNotes = this.doSaveNotes.bind(this);
        this.handleEditNotesClick = this.handleEditNotesClick.bind(this);
        this.goToPaymentId = this.goToPaymentId.bind(this);
    }

    componentDidMount() {
        const self = this;

        self._isMounted = true;
        self.doLoad();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    doLoad() {
        const self = this;
        if (!self._isMounted) {
            return;
        }
        const requestId = self.state.requestId + 1;

        const data = {PaymentId: self.state.paymentId, ListSource: self.props.listSource};

        new Promise(resolve => {
            self.setState({action: PAYMENTINFO_ACTION.LOADING, errors: null, requestId: requestId}, resolve);
        })
        .then(() => {
            if (self._isMounted) {
                return MakeApiCall(
                    $.get(
                        {
                            url: '/api/admin/getPaymentInfo',
                            data: data,
                            dataType: 'json',
                            cache: false
                        }
                    ),
                    false,
                    () => !self._isMounted || (self.state.requestId != requestId)
                );
            }
        })
        .then(response => {
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({
                    action: PAYMENTINFO_ACTION.NONE,
                    isLoaded: true,
                    paymentInfo: response,
                });
            }
        })
        .catch(errors => {
            WriteConsoleErrors(errors, true, false);
            if (self._isMounted && (self.state.requestId == requestId)) {
                self.setState({action: PAYMENTINFO_ACTION.NONE, errors: errors});
            }
        });
    }

    goToPaymentId(paymentId) {
        const self = this;

        if (self._isMounted) {
            ReplaceUrl(UrlBuilder.PaymentInfo({PaymentId: paymentId, ListSource: self.props.listSource}));
    
            self.setState({paymentId: paymentId}, self.doLoad);
        }
    }

    handleEditNotesClick() {
        this.setState({action: PAYMENTINFO_ACTION.EDITNOTES});
    }

    doSaveNotes(notes) {
        const self = this;
        const data = {PaymentId: self.state.paymentId, Notes: notes};
        const verificationToken = self.state.paymentInfo.VerificationToken;

        const result = MakeApiCall(
            $.post(
                {
                    url: '/api/admin/savePaymentNotes',
                    headers: {RequestVerificationToken: verificationToken},
                    data: JSON.stringify(data),
                    contentType: 'application/json',
                    dataType: 'json',
                    cache: false
                }
            )
        )
        .then(response => {
            if (self._isMounted) {
                const paymentInfo = {...self.state.paymentInfo, AdminNotes: notes};
                self.setState({paymentInfo: paymentInfo});
            }

            return response;
        });

        return result;
    }

    handleEditNotesDialogHide() {
        const self = this;

        if (self.state.action == PAYMENTINFO_ACTION.EDITNOTES) {
            self.setState({action: PAYMENTINFO_ACTION.NONE});
        }
    }

    render() {
        const self = this;
        const {paymentId, paymentInfo, action, isLoaded, errors} = self.state;
        const {timeZoneId, listSource} = self.props;

        const isDisabled = action != PAYMENTINFO_ACTION.NONE;

        let logMessage = '';
        if (isLoaded && Boolean(paymentInfo.LogMessage)) {
            try {
                logMessage = JSON.stringify(JSON.parse(paymentInfo.LogMessage), null, 4);
            }
            catch (error) 
            {
                logMessage = paymentInfo.LogMessage;
            }
        }

        const buttonPrev = Boolean(paymentInfo) &&
            <Link 
                key="buttonPrev"
                className="btn btn-outline-secondary btn-sm newstyle" 
                disabled={isDisabled || !Boolean(paymentInfo.PrevId)} 
                to={() => self.goToPaymentId(paymentInfo.PrevId)}
            >
                Prev
            </Link>

        const buttonNext = Boolean(paymentInfo) &&
            <Link 
                key="buttonNext"
                className="btn btn-outline-secondary btn-sm newstyle" 
                disabled={isDisabled || !Boolean(paymentInfo.NextId)} 
                to={() => self.goToPaymentId(paymentInfo.NextId)}
            >
                Next
            </Link>

        return (
            <div>
                <PageHeader
                    fixed
                    captionTruncate
                    notWrappedMaxHeight={75}
                    caption={
                        <h2 className="text-truncate">                                    
                            <span className="mr-2">Payment #{paymentId}</span>
                            {Boolean(paymentInfo) && (paymentInfo.Id == paymentId) &&
                                <>
                                    {paymentInfo.IsAuto && <span className="text-warning mr-2">(auto)</span>}
                                    {paymentInfo.IsSuccess && 
                                        <Link to={UrlBuilder.PaymentPaper({PaymentId: paymentInfo.Id})} target="_blank" disabled={isDisabled} className="mr-2">
                                            <span className="fa fa-file-pdf-o" />
                                        </Link>
                                    }
                                </>
                            }
                            <RefreshButtonComponent 
                                disabled={action == PAYMENTINFO_ACTION.LOADING} 
                                animated={action == PAYMENTINFO_ACTION.LOADING} 
                                onClick={self.doLoad} 
                                className="fs-085"
                            />
                        </h2>
                    }
                    buttons={[<div key="buttonGroup" className="btn-group">{[buttonPrev, buttonNext].filter(Boolean)}</div>]}
                />
                <ErrorsBlockComponent errors={errors} />
                {isLoaded && (action == PAYMENTINFO_ACTION.EDITNOTES) &&
                    <EditNotesDialogComponent
                        title={<span className="fs-125"><span className="mr-1 fa fa-edit" />Notes for payment #{paymentInfo.Id}</span>}
                        notes={paymentInfo.AdminNotes}
                        doSave={self.doSaveNotes}
                        onHide={self.handleEditNotesDialogHide}
                    />
                }
                {isLoaded &&
                    <div>
                        <InfoItem caption="Account" content={<AccountComponent id={paymentInfo.AccountId} name={paymentInfo.AccountName} disabled={isDisabled} showId showLoginAs linkToInfo />} />
                        {(paymentInfo.Amount >= 0) && <InfoItem caption="Amount" content={<b>{numeral(paymentInfo.Amount).USD()}</b>} />}
                        {(paymentInfo.Amount < 0) && <InfoItem caption="Amount" content={<b className="text-danger">{numeral(paymentInfo.Amount).USD()}</b>} />}
                        <InfoItem caption="Fee" content={(paymentInfo.Fee != null) ? numeral(paymentInfo.Fee).USD() : '??'} />
                        <InfoItem caption="User IP" content={paymentInfo.UserIP} />
                        <InfoItem caption="Created" content={<DateTimeItem utcDate={paymentInfo.CreatedAt} timeZoneId={timeZoneId} format="L LTS (z)" />} />
                        <InfoItem caption="State" content={<span className={PAYMENT_STATE.textClass(paymentInfo.StateId)}>{paymentInfo.StateName}</span>} />
                        {paymentInfo.IsSuccess && <InfoItem caption="Success" content={<DateTimeItem utcDate={paymentInfo.SuccessAt} timeZoneId={timeZoneId} format="L LTS (z)" />} />}
                        {paymentInfo.IsError && <InfoItem caption="Error" content={<DateTimeItem utcDate={paymentInfo.ErrorAt} timeZoneId={timeZoneId} className="text-danger" format="L LTS (z)" />} />}
                        <InfoItem caption="Payment type" content={paymentInfo.PaymentTypeName} />
                        {Boolean(paymentInfo.CreditCard) &&
                            <InfoItem
                                caption="Credit card"
                                content={
                                    <CreditCardCaptionComponent 
                                        cardNumber={paymentInfo.CreditCard.ShortDisplayNumber} 
                                        cardBrandName={paymentInfo.CreditCard.BrandName} 
                                        showIcon
                                        showBrand
                                        showNumber
                                    />
                                }
                            />
                        }
                        {Boolean(paymentInfo.StripePaymentIntentId) &&
                            <InfoItem 
                                caption="Transaction #" 
                                content={<a href={`https://dashboard.stripe.com/payments/${paymentInfo.StripePaymentIntentId}`} target="_blank">{paymentInfo.TransactionId}</a>} 
                            />
                        }
                        {!Boolean(paymentInfo.StripePaymentIntentId) &&
                            <InfoItem caption="Transaction #" content={paymentInfo.TransactionId} />
                        }
                        <InfoItem caption="Description" content={paymentInfo.Description} />
                        {Boolean(logMessage) && <InfoItem caption="Last log message" content={<textarea className="form-control compact text-body bg-white" rows={5} readOnly value={logMessage} />} vertical />}
                        <InfoItem caption="Notes" content={<textarea className="form-control compact text-body bg-white" rows={2} readOnly value={paymentInfo.AdminNotes || ''} />} vertical />
                    </div>
                }
                {isLoaded &&
                    <div className="pt-4">
                        <button 
                            type="button"
                            className="btn btn-outline-primary btn-sm newstyle"
                            disabled={isDisabled}
                            onClick={self.handleEditNotesClick}
                        >
                            <span className="mr-1 fa fa-edit" />Edit notes
                        </button>
                    </div>
                }
            </div>
        )
    }
}
PaymentInfoComponent.propTypes = {
    paymentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    timeZoneId: PropTypes.string.isRequired,
    listSource: PropTypes.string,
}